// Tools.jsx
import React from 'react';
import Recorder from 'recorder-js';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import AudioRecorder from '../../utilities/AudioRecorder';
import SmartPaste from '../../utilities/SmartPaste';
import { workflowAI } from '../../utilities/WorkflowAI';
import axios from 'axios';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

const formatTimeStamp = (dateString) => {
  const date = new Date(dateString);
  const formattedDate = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formattedTime = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${formattedDate} on ${formattedTime}`;
};

function generateId() {
  return Math.random().toString(36).slice(2, 11); // random 9-char id
}

function toArray(x) {
  return Array.isArray(x) ? x : (x ? [x] : []);
}

function regenerateIds(problems) {
  return toArray(problems).map(p => ({
    ...p,
    id: p.id === null ? generateId() : p.id,
    goal: toArray(p.goal).map(g => ({
      ...g,
      id: g.id === null ? generateId() : g.id
    })),
    plan: toArray(p.plan).map(pl => ({
      ...pl,
      id: pl.id === null ? generateId() : pl.id
    })),
    review: toArray(p.review).map(r => ({
      ...r,
      id: r.id === null ? generateId() : r.id
    })),
    journey: toArray(p.journey).map(j => ({
      ...j,
      id: j.id === null ? generateId() : j.id
    })),
  }));
}



class Tools extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      referrences: [],
      dashboard: [],
      formData: this.props.patientData,
      adminData: this.props.adminData,
      isLoading: false,
      AInote: "",
      isProcessing: false,
      credits: 0,
      // NEW
      toggles: {
        mhcpEnabled: false,
        healthAssessment: false,
        carePlan: false,
      }
    };

    this.recorder = new Recorder(new AudioContext(), { format: 'wav' });
    this.menuRef = React.createRef();
  }

  componentDidMount() {
    const credits = document.getElementById('creditLeft')?.innerText ?? '0';
    this.setState({ credits });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.patientData !== this.props.patientData) {
      this.setState({ formData: this.props.patientData });
    }
    if (prevProps.adminData !== this.props.adminData) {
      this.setState({ adminData: this.props.adminData });
    }
  }

  toggleFlag = (key) => {
    this.setState((prev) => ({
      toggles: { ...prev.toggles, [key]: !prev.toggles[key] },
    }));
  };

  updatePatientData = () => {
    this.props.updatePatientData(this.state.formData);
  };

  handlePaste = async (pasteData) => {
    this.handleInputUpdate(pasteData);
    await axios.post(`${EndPoint}/log/event`, { type: 'Smart Paste', credit: 5 }, { withCredentials: true });
  };

  handleInputUpdate = async (transcription) => {
    this.setState({ isProcessing: true });

    try {
      const { mhcpEnabled, healthAssessment, carePlan } = this.state.toggles;
      const initData = this.state.formData;

      // --- Consultations ---
      let response;
      try {
        response = await axios.post(`${EndPoint}/careplan/consultation`, { transcription }, {
          withCredentials: true,
          responseType: 'json'
        });
      } catch (e) {
        toast.error(`Unable to write consultation note`, { position: "top-right", autoClose: 3000 });
        throw e;
      }

      if (response.data?.success) {
        toast.success(`Consultation notes written`, { position: "top-right", autoClose: 3000 });
      } else {
        toast.error(`Unable to write consultation note`, { position: "top-right", autoClose: 3000 });
      }

      let consultObj;
      try {
        consultObj = typeof response.data.success === 'string'
          ? JSON.parse(response.data.success)
          : response.data.success;
      } catch (e) {
        console.error('Failed to parse consultation JSON:', e, response.data.success);
        consultObj = { consultation: '', tag: '', instruction: '' };
      }

      this.setState(prevState => ({
        formData: {
          ...prevState.formData,
          consultations: [
            ...(prevState.formData.consultations || []),
            {
              id: `consult${generateId()}`,
              dateAdded: new Date().toISOString(),
              consultation: consultObj.consultation,
              tag: consultObj.tag,
              instruction: consultObj.instruction
            }
          ]
        }
      }));

      // --- MHCP ---
      if (mhcpEnabled) {
        let response2;
        try {
          response2 = await axios.post(`${EndPoint}/careplan/mhcp`, { transcription, initData }, {
            withCredentials: true,
            responseType: 'json'
          });
        } catch (e) {
          toast.error(`Unable to update Mental Health Care Plan`, { position: "top-right", autoClose: 3000 });
          throw e;
        }

        if (response2.data?.success) {
          toast.success(`Mental Health Care Plan updated`, { position: "top-right", autoClose: 3000 });
        } else {
          toast.error(`Unable to update Mental Health Care Plan`, { position: "top-right", autoClose: 3000 });
        }

        let data;
        try {
          data = typeof response2.data.success === 'string'
            ? JSON.parse(response2.data.success)
            : response2.data.success;
        } catch (e) {
          console.error('Failed to parse MHCP JSON:', e, response2.data.success);
          data = {};
        }

        const date = new Date();
        const formattedDate = date.toISOString().split('T')[0];
        const stamp = data.timeStamp ? data.timeStamp : formattedDate;

        const mhcpData = {
          presentingIssue: data.presentingIssue,
          history: data.history,
          pastHistory: data.pastHistory,
          childhoodHistory: data.childhoodHistory,
          familyHistory: data.familyHistory,
          parentChildneed: data.parentChildNeed,
          occupation: data.occupation,
          social: data.social,
          substance: data.substance,
          currentMedication: data.currentMedication,
          allergies: data.allergies,
          treatmentHistory: data.treatmentHistory,
          medicationHistory: data.medicationHistory,
          relevantExamination: data.relevantExamination,
          relevantInvestigation: data.relevantInvestigation,
          devTesting: data.devTesting,
          mentalStateExamination: data.mentalStateExamination,
          risk: data.risk,
          tools: data.tools,
          diagnosis: data.diagnosis,
          date: stamp
        };

        const mhformulationData = {
          predisposing: data.predisposing,
          precipitating: data.precipitating,
          perpetuating: data.perpetuating,
          protective: data.protective,
          date: stamp
        };

        const mhsafetyData = {
          reason: data.reason,
          cando: data.cando,
          help: data.help,
          call: data.call,
          talk: data.talk,
          professional: data.professional,
          date: stamp
        };

        const relapsePreventionData = {
          triggers: data.triggers,
          warningSigns: data.warningSigns,
          copingStrategy: data.copingStrategy,
          support: data.support,
          emergency: data.emergency
        };

        this.setState(prevState => ({
          formData: {
            ...prevState.formData,
            mhcp: mhcpData,
            mhformulation: mhformulationData,
            mhsafety: mhsafetyData,
            relapsePrevention: relapsePreventionData
          }
        }));
      }


      // --- Health Assessment
      if (healthAssessment) {
        let response3;
        try {
          response3 = await axios.post(`${EndPoint}/careplan/ha`, { transcription, initData }, {
            withCredentials: true,
            responseType: 'json'
          });
        } catch (e) {
          toast.error(`Unable to update Health Assessment`, { position: "top-right", autoClose: 3000 });
          throw e;
        }

        if (response3.data?.success) {
          toast.success(`Health Assessment updated`, { position: "top-right", autoClose: 3000 });
        } else {
          toast.error(`Unable to update Health Assessment`, { position: "top-right", autoClose: 3000 });
        }

        let data;
        try {
          data = typeof response3.data.success === 'string'
            ? JSON.parse(response3.data.success)
            : response3.data.success;
        } catch (e) {
          console.error('Failed to parse HA JSON:', e, response3.data.success);
          data = {};
        }

        const date = new Date();
        const formattedDate = date.toISOString().split('T')[0];
        const stamp = data.timeStamp ? data.timeStamp : formattedDate;

        const HAData = {
          medicalHistory: data.medicalhistory,
          pastMedical: data.pastmedical,
          allergies: data.allergies,
          familyHistory: data.familyhistory,
          alcohol: data.alcohol,
          smoking: data.smoking,
          substance: data.substance,
          medications: data.medications,
          medicationArrangement: data.medicationarrangement,
          vision: data.vision,
          hearing: data.hearing,
          fallAndHomeSafety: data.fallandhomesafety,
          skin: data.skin,
          feet: data.feet,
          nutrition: data.nutrition,
          oralHealth: data.oralhealth,
          cognition: data.cognition,
          mentalHealth: data.mentalhealth,
          urineContinence: data.urineContinence,
          faecalContinence: data.faecalContinence,
          mobility: data.mobility,
          activity: data.activity,
          social: data.social,
          housing: data.housing,
          legal: data.legal,
          finance: data.finance,
          driving: data.driving,
          advancedCareDirective: data.advancedcaredirective,
          services: data.services,
          examination: data.examination,
          surveillance: data.surveillance,
          vaccination: data.vaccination, 
          date: stamp
        };

      
        this.setState(prevState => ({
          formData: {
            ...prevState.formData,
            healthassessment: HAData,
     
          }
        }));
      }

 
      // --- Care plan
      if (carePlan) {
        let response4;
        try {
          response4 = await axios.post(`${EndPoint}/careplan/plan`, { transcription, initData }, {
            withCredentials: true,
            responseType: 'json'
          });
        } catch (e) {
          toast.error(`Unable to update Care plan`, { position: "top-right", autoClose: 3000 });
          throw e;
        }

        if (response4.data?.success) {
          toast.success(`Care plan updated`, { position: "top-right", autoClose: 3000 });
        } else {
          toast.error(`Unable to update Care plan`, { position: "top-right", autoClose: 3000 });
        }

        var planData;
        try {
          planData = typeof response4.data.success === 'string'
            ? JSON.parse(response4.data.success)
            : response4.data.success;
        } catch (e) {
          console.error('Failed to parse Care Plan JSON:', e, response4.data.success);
          planData = [];
        }

        this.setState(prevState => ({
          formData: {
            ...prevState.formData,
            planning: planData,
     
          }
        }));
      }


           // Persist changes
      this.updatePatientData();

    } catch (err) {
      console.error('handleInputUpdate error:', err);
    } finally {
      this.setState({ isProcessing: false });
    }
  };


  setMode = (mode) => this.setState({ mode });

  renderToggleGroup() {
    const { mode } = this.state;
    const options = ['MHCP', 'Health Assessment', 'Care Plan'];

    return (
      <div className="flex items-center gap-1 ml-3">
        {options.map((opt) => {
          const active = mode === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => this.setMode(opt)}
              className={[
                'px-3 py-2 text-sm rounded-md border transition',
                active
                  ? 'bg-sky-700 text-white border-sky-800 shadow'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              ].join(' ')}
              aria-pressed={active}
              title={opt}
            >
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  renderToggleGroup() {
    const { toggles } = this.state;
    const items = [
      { key: 'mhcpEnabled', label: 'Mental Health' },
      { key: 'healthAssessment', label: 'Health Assessment' },
      { key: 'carePlan', label: 'Care Plan' },
    ];

    return (
      <div className="flex items-center gap-2 ml-4">
        {items.map(({ key, label }) => {
          const active = !!toggles[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => this.toggleFlag(key)}
              aria-pressed={active}
              className={[
                'px-3 py-2 w-72 h-14 text-xs rounded-md border transition select-none',
                active
                  ? 'bg-sky-700 text-white border-sky-800 shadow'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
              ].join(' ')}
              title={label}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  }


  render() {
    const { credits, isProcessing } = this.state;
    const isDisabled = credits === '0';

    return (
      <div
        className="col-span-6 grid-rows-4 xs:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 no-print"
        style={{ gridTemplateRows: 'minmax(320px, auto)' }}
      >
        <div className={`col-span-6 grid grid-cols-4 bg-slate-200 m-2 rounded ${isDisabled ? 'opacity-25 pointer-events-none' : ''}`}>
        {this.renderToggleGroup()}
          {/* Left: SmartPaste */}
          <div className={`place-self-end z-20 relative ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}>
            <SmartPaste onPasteData={this.handlePaste} />
          </div>

          {/* Right: AudioRecorder + inline toggle */}
          <div className={`place-self-start -ml-80 sm:-ml-[700px] relative ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="flex items-center ml-80">
              {/* AudioRecorder renders the microphone button */}
              <AudioRecorder onInputUpdate={this.handleInputUpdate} />
              {/* Toggle group on the same line */}

            </div>
          </div>
        </div>

        <ToastContainer />
      </div>
    );
  }
}

export default Tools;
